import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Modal } from "react-bootstrap";
import TableDetails from "./Widget/TableDetails";
import { RemoveIcon } from "../../assets/images";
import { Responsive, WidthProvider } from "react-grid-layout";
import BarChart from "./Widget/WidgetType/BarChart";
import DonutChart from "./Widget/WidgetType/DonutChart";
import PieChart from "./Widget/WidgetType/PieChart";
import LineChart from "./Widget/WidgetType/LineChart";
import SingleValueKPI from "./Widget/WidgetType/SingleValueKPI";
import SingleValueWithDetailKPI from "./Widget/WidgetType/SingleValueWithDetailKPI";
import MultipleDetailKPI from "./Widget/WidgetType/MultipleDetailKPI";
import BackupActivityTimeline from "./Widget/WidgetType/BackupActivityTimeline";
import DynamicWidgetList from "./DynamicWidgetList";
import AlertTemplateMessage from "../Configuration/AlertTemplate/AlertTemplateMessage";
import {
  getAlertDataDetailed,
  getConsolidatedDataDetailed,
  getDeviceDataDetailed,
  getVideoBackupDataDetailed
} from "../../services/dashboardService";
import { createExportRequestParams } from "./DashboardUtils";

const ResponsiveGridLayout = WidthProvider(Responsive);

const DynamicWidgetStructure = ({
  isDarkTheme,
  open,
  setOpen,
  widgetList,
  setAlertsPayload,
  setConsolidatedDataPayload,
  setDeviceStatusPayload,
  setVBDataPayload,
  setVBTimeLineDataPayload,
  consolidatedData,
  deviceStatus,
  alertsData,
  VBData,
  VBTimeLineData,
  selectedDate,
  setSelectedDate,
  filteredLayout,
  apiFilter,
}) => {
  const [layout, setLayout] = useState([]);
  const [items, setItems] = useState([]);
  const [counter, setCounter] = useState(0);
  const [show, setShow] = useState();
  const [searchText, setSearchText] = useState("");
  const [widgetListData, setWidgetListData] = useState([]);

  const [tableData, setTableData] = useState([]);
  const [tableDataLoading, setTableDataLoading] = useState(false);
  const [tableDataError, setTableDataError] = useState(null);
  const [tableRequestParams, setTableRequestParams] = useState(null);
  const [csvFileName, setCSVFileName] = useState();
  const [selectedWidgetName, setSelectedWidgetName] = useState("");

  
  useEffect(() => {
    const safeSpread = (arr) =>
      Array.isArray(arr) && arr.length > 0 ? arr : [];
    // Flatten alertsData object (grouped by DateRange) into a single array
    const flattenAlertsData = (alertsDataObj) => {
      if (!alertsDataObj || typeof alertsDataObj !== "object") return [];
      return Object.values(alertsDataObj).flat();
    };
    setWidgetListData([
      ...safeSpread(consolidatedData),
      ...safeSpread(deviceStatus),
      ...safeSpread(flattenAlertsData(alertsData)),
      ...safeSpread(VBData),
      ...safeSpread(VBTimeLineData),
    ]);
  }, [consolidatedData, deviceStatus, alertsData, VBData]);

  const BarAlertByType = {
    title: "Alert By Type",
    dataValues: [70, 60, 80, 85, 60, 60, 45],
    labels: ["Manual Alerts", "Zone Alert", "Face Detection"],
    gradientColorOne: "#8400ff",
    gradientColorTwo: "#f5208a",
    xName: "Alert Type",
    yName: "Alert Count",
  };

  const DonutWidgetDetails = {
    title: "Alert by severity",
    data: [35, 48, 27, 40],
    labels: ["Extreme", "High", "Moderate", "Low"],
    colorCodes: [
      ["#dd2121", "#fa4646"],
      ["#d06841", "#fa6028"],
      ["#daa926", "#f7b607"],
      ["#83ba2b", "#96eb0e"],
    ],
  };

  const PieWidgetDetails = {
    title: "Alert By Status",
    data: [300, 50, 100],
    labels: ["Open", "Acknowledgement", "Closed"],
    colorCodes: [
      ["rgb(220, 138, 146)", "rgb(240, 53, 71)"],
      ["rgb(120, 142, 224)", "rgb(58, 99, 249)"],
      ["rgb(146, 225, 165)", "rgb(10, 235, 63)"],
    ],
  };

  const LineWidgetDetails = {
    labels: ["Mon", "Tues", "Wed", "Thurs", "Fri", "Sat", "Sun"],
    dataPoints: [65, 59, 80, 81, 56, 55, 40],
    borderColor: "rgb(75, 192, 192)",
    label: "Alerts",
    tension: 0.1,
    title: "Alerts by site using line chart",
  };


  const getHeightByWidgetType = (type) => {
    switch (type) {
      case "SingleKPI":
      case "SingleKPI-Detailed":
        return 4;
      case "multikpi":
        return 9;
      case "TimeLine":
        return 14; // wider timeline widget
      default:
        return 8; // fallback height
    }
  };

  const getWidthByWidgetType = (type) => {
    switch (type) {
      case "SingleKPI":
      case "SingleKPI-Detailed":
        return 2; // Small widgets
      case "TimeLine":
        return 8; // wider timeline widget
      default:
        return 4; // Default width
    }
  };

  const getMinWidthByType = (type) => {
    switch (type) {
      case "SingleKPI":
      case "SingleKPI-Detailed":
        return 2;
      case "TimeLine":
        return 6;
      default:
        return 2;
    }
  };

  const getMinHeightByType = (type) => {
    switch (type) {
      case "SingleKPI":
      case "SingleKPI-Detailed":
        return 4;
      case "TimeLine":
        return 10;
      default:
        return 6;
    }
  };

  const updateWidgetPayload = useCallback((newItems) => {
    // Map newItems to widgetList by WidgetID
    const mappedWidgets = Array.isArray(newItems)
      ? newItems
          .map((item) =>
            widgetList.find((w) => String(w.WidgetID) === String(item.i))
          )
          .filter(Boolean) // Remove undefined if any i doesn't match
      : [];

    const alertWidgets = mappedWidgets
      .filter((w) => w.Entity === "Alert")
      .map((w) => ({
        WidgetID: w.WidgetID,
        WidgetType: w.WidgetType,
        Datapoints: w.Datapoints,
      }));

    const deviceStatusWidgets = mappedWidgets
      .filter((w) => w.Entity === "DeviceStatus")
      .map((w) => ({
        WidgetID: w.WidgetID,
        WidgetType: w.WidgetType,
        Datapoints: w.Datapoints,
      }));

    const masterWidgets = mappedWidgets
      .filter((w) => w.Entity === "Master")
      .map((w) => ({
        WidgetID: w.WidgetID,
        WidgetType: w.WidgetType,
        Datapoints: w.Datapoints,
      }));

    const videoBackupWidgets = mappedWidgets
      .filter((w) => w.Entity === "Video Backup")
      .map((w) => ({
        WidgetID: w.WidgetID,
        WidgetType: w.WidgetType,
        Datapoints: w.Datapoints,
      }));

    const timelineWidgets = mappedWidgets
      .filter((w) => w.Entity === "Video Backup Timeline")
      .map((w) => ({
        WidgetID: w.WidgetID,
        WidgetType: w.WidgetType,
        Datapoints: w.Datapoints,
      }));

    setAlertsPayload((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(alertWidgets)) {
        return alertWidgets;
      }
      return prev;
    });
    setDeviceStatusPayload((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(deviceStatusWidgets)) {
        return deviceStatusWidgets;
      }
      return prev;
    });
    setVBDataPayload((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(videoBackupWidgets)) {
        return videoBackupWidgets;
      }
      return prev;
    });
    setConsolidatedDataPayload((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(masterWidgets)) {
        return masterWidgets;
      }
      return prev;
    });
    setVBTimeLineDataPayload((prev) => {
      if (JSON.stringify(prev) !== JSON.stringify(timelineWidgets)) {
        return timelineWidgets;
      }
      return prev;
    });
  }, [widgetList, setAlertsPayload, setDeviceStatusPayload, setConsolidatedDataPayload, setVBDataPayload, setVBTimeLineDataPayload]);

  useEffect(() => {
    // Try to get layout from API or props (filteredLayout)
    try {
      const savedLayout = localStorage.getItem("dashboard-layout");
      const savedItems = localStorage.getItem("dashboard-items");
      const savedCounter = localStorage.getItem("dashboard-counter");
      console.log("filteredLayout",filteredLayout)
      if (
        filteredLayout &&
        Array.isArray(filteredLayout.layout) &&
        Array.isArray(filteredLayout.items)
      ) {
        setLayout(filteredLayout.layout);
        setItems(filteredLayout.items);
        setCounter(filteredLayout.counter || 0);
        updateWidgetPayload(filteredLayout.items);
      } else if (savedLayout && savedItems) {
        setLayout(JSON.parse(savedLayout));
        setItems(JSON.parse(savedItems));
        setCounter(parseInt(savedCounter) || 0);
        updateWidgetPayload(JSON.parse(savedItems));
      }
    } catch (err) {
      // On error, fallback to localStorage
      const savedLayout = localStorage.getItem("dashboard-layout");
      const savedItems = localStorage.getItem("dashboard-items");
      const savedCounter = localStorage.getItem("dashboard-counter");
      if (savedLayout && savedItems) {
        setLayout(JSON.parse(savedLayout));
        setItems(JSON.parse(savedItems));
        setCounter(parseInt(savedCounter) || 0);
        updateWidgetPayload(JSON.parse(savedItems));
      }
    }
  }, [filteredLayout, updateWidgetPayload]);


// Format helper
function formatDateTime(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const hh = String(date.getHours()).padStart(2, '0');
  const mm = String(date.getMinutes()).padStart(2, '0');
  const ss = String(date.getSeconds()).padStart(2, '0');
  return `${y}-${m}-${d} ${hh}:${mm}:${ss}`;
}

  const handleAddWidget = (WidgetID) => {
    if (items.some((item) => item.i === WidgetID)) return;
    const widgetDataObj = widgetList.find((w) => w.WidgetID === WidgetID);

    const widgetWidth = getWidthByWidgetType(widgetDataObj.WidgetType);
    const widgetHeight = getHeightByWidgetType(widgetDataObj.WidgetType);
    const maxCols = 12;

    // Find the first available spot in the grid
    let xPos = 0;
    let yPos = 0;

    outer: for (let y = 0; y < 100; y++) {
      // 100 rows max, adjust as needed
      for (let x = 0; x <= maxCols - widgetWidth; x++) {
        // Check if this space is free
        const collision = layout.some(
          (item) =>
            item.y < y + widgetHeight &&
            item.y + item.h > y &&
            item.x < x + widgetWidth &&
            item.x + item.w > x
        );
        if (!collision) {
          xPos = x;
          yPos = y;
          break outer;
        }
      }
    }

    const newItem = {
      i: String(WidgetID),
      x: xPos,
      y: yPos,
      w: widgetWidth,
      h: widgetHeight,
      // minW: 2,
      // minH: 4,
      minW: getMinWidthByType(widgetDataObj.WidgetType),
      minH: getMinHeightByType(widgetDataObj.WidgetType),
    };

    const newLayout = [...layout, newItem];
    const newItems = [...items, { i: String(WidgetID) }];

    setLayout(newLayout);
    setItems(newItems);
    setCounter(counter + 1);
    saveToStorage(newLayout, newItems, counter + 1);
  };

  const handleRemoveWidget = (keyToRemove) => {
    const newLayout = layout.filter((item) => item.i !== keyToRemove);
    const newItems = items.filter((item) => item.i !== keyToRemove);
    setLayout(newLayout);
    setItems(newItems);
    saveToStorage(newLayout, newItems, counter);
  };

  const onLayoutChange = (newLayout) => {
    setLayout(newLayout);
    saveToStorage(newLayout, items, counter);
    // setLayout(currentLayout)
  };

  const saveToStorage = (layoutToSave, itemsToSave, counterToSave) => {
    localStorage.setItem("dashboard-layout", JSON.stringify(layoutToSave));
    localStorage.setItem("dashboard-items", JSON.stringify(itemsToSave));
    localStorage.setItem("dashboard-counter", counterToSave);
  };

  const handleModalShow = (filter, API, WidgetID, csvFileName = "table-data", widgetName = "", Datapoints = []) => {
    setShow(true);
    setTableDataLoading(true);
    setTableDataError(null);
    setCSVFileName(csvFileName);
    setSelectedWidgetName(widgetName);

    const user = JSON.parse(localStorage.getItem("user"));
    const UserID = user?.Id;

    // Find the widget by WidgetID
    const widget = widgetList.find(
      (w) => String(w.WidgetID) === String(WidgetID)
    );
    let FromDateTime = "";
    let ToDateTime = "";

    console.log({ widget });

    // If widget has DateRange, calculate dates
    if (widget && widget.DateRange) {
      const now = new Date();
      // ToDateTime: today at 23:59:59
      const toDate = new Date(
        now.getFullYear(),
        now.getMonth(),
        now.getDate(),
        23,
        59,
        59,
        999
      );
      ///FromDateTime: (today - (DateRange - 1)) at 00:00:00
      const fromDate = new Date(toDate);
      fromDate.setDate(toDate.getDate() - (widget.DateRange - 1));
      fromDate.setHours(0, 0, 0, 0);

      const pad = (n) => n.toString().padStart(2, "0");
      const formatDate = (date) =>
        date.getFullYear() +
        "-" +
        pad(date.getMonth() + 1) +
        "-" +
        pad(date.getDate()) +
        " " +
        pad(date.getHours()) +
        ":" +
        pad(date.getMinutes()) +
        ":" +
        pad(date.getSeconds());

      FromDateTime = formatDate(fromDate);
      ToDateTime = formatDate(toDate);
    }

    if (API === "Alert") {
      const payload = {
        Filter: filter,
        UserID: UserID,
        FromDateTime,
        ToDateTime,
        filters: apiFilter || {},
      };
      setTableRequestParams(createExportRequestParams(payload, "Alert"));
      getAlertDataDetailed(payload)
        .then((res) => {
          if (res && res.Status === "success") {
            setTableData(res.Data || []);      
          } else {
            setTableDataError("Failed to load table data.");
          }
          setTableDataLoading(false);
        })
        .catch((err) => {
          setTableDataError("Error loading table data.");
          setTableDataLoading(false);
          console.log("Error in TableDetails component: ", err);
        });
    } else if (API === "Master") {
      const payload = {
        Filter: filter,
        UserID: UserID,
        FromDateTime,
        ToDateTime,
        filters: apiFilter || {},
      };
      setTableRequestParams(createExportRequestParams(payload, "Master"));
      getConsolidatedDataDetailed({
        Filter: filter,
        UserID: UserID,
        filters: apiFilter || {},
      })
        .then((res) => {
          if (res && res.Status === "success") {
            setTableData(res.Data || []);
          } else {
            setTableDataError("Failed to load table data.");
          }
          setTableDataLoading(false);
        })
        .catch((err) => {
          setTableDataError("Error loading table data.");
          setTableDataLoading(false)
          console.log("Error in TableDetails component: ", err);
        });
    } else if (API === "Video Backup") {


        const now = new Date();

        // FromDateTime: 24 hours ago
        const fromDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);

        const FromDateTime = formatDateTime(fromDate);
        const ToDateTime   = formatDateTime(now);
          
      const payload = {
        Filter: filter,
        UserID: UserID,
        FromDateTime,
        ToDateTime,
        filters: apiFilter || {},
      };
      setTableRequestParams(createExportRequestParams(payload, "Video Backup"));
      getVideoBackupDataDetailed({
        UserID: UserID,
        Filter: filter,
        FromDateTime,
        ToDateTime,
        filters: apiFilter || {}
      })
        .then((res) => {
          if (res && res.Status === "success") {
            setTableData(res.Data || []);
          } else {
            setTableDataError("Failed to load table data.");
          }
          setTableDataLoading(false);
        })
        .catch((err) => {
          setTableDataError("Error loading table data.");
          setTableDataLoading(false);
          console.log("Error in TableDetails component: ", err);
        });
    } else if (API === "DeviceStatus") {
      const payload = {
        Filter: filter,
        UserID: UserID,
        FromDateTime,
        ToDateTime,
        filters: apiFilter || {},
      };
      setTableRequestParams(createExportRequestParams(payload, "DeviceStatus"));
      getDeviceDataDetailed({
        UserID: UserID,
        Filter: filter,
        filters: apiFilter || {}
      })
        .then((res) => {
          if (res && res.Status === "success") {
            setTableData(res.Data || []);
          } else {
            setTableDataError("Failed to load table data.");
          }
          setTableDataLoading(false);
        })
        .catch((err) => {
          setTableDataError("Error loading table data.");
          setTableDataLoading(false);
          console.log("Error in TableDetails component: ", err);
        });
    } else {
      setTableData([]);
      setTableDataLoading(false);
    }
  };
  const handleSelectAllWidgets = ({
    checked,
    data,
    items,
    layout,
    counter,
    getWidthByWidgetType,
    getHeightByWidgetType,
    setItems,
    setLayout,
    setCounter,
    saveToStorage,
  }) => {
    if (checked) {
      const newItems = [];
      const newLayout = [];
      let newCounter = counter;
      let xPos = 0;
      let yPos = 0;
      const maxCols = 12;

      data.forEach((widget) => {
        const { WidgetID, WidgetType } = widget;
        if (!items.some((item) => item.i === String(WidgetID))) {

          const widgetWidth = getWidthByWidgetType(WidgetType);
          const widgetHeight = getHeightByWidgetType(WidgetType);

          // If widget doesn't fit in current row, move to next row
          if (xPos + widgetWidth > maxCols) {
            xPos = 0;
            yPos += 1;
          }

          newLayout.push({
            i: String(WidgetID),
            x: xPos,
            y: yPos,
            w: widgetWidth,
            h: widgetHeight,
            // minW: 2,
            // minH: 4,
            minW: getMinWidthByType(WidgetType),
            minH: getMinHeightByType(WidgetType),
          });
          newItems.push({ i: String(WidgetID) });
          newCounter++;
          xPos += widgetWidth;
        }
      });

      const combinedItems = [...items, ...newItems];
      const combinedLayout = [...layout, ...newLayout];

      setItems(combinedItems);
      setLayout(combinedLayout);
      setCounter(newCounter);
      saveToStorage(combinedLayout, combinedItems, newCounter);
      updateWidgetPayload(combinedItems);
    } else {
      setItems([]);
      setLayout([]);
      setCounter(0);
      saveToStorage([], [], 0);
      updateWidgetPayload([]);
    }
  };

  const getWidgetLabel = (i) => {
    // Find the matching widgetData object by WidgetID
    const widgetDataObj = widgetListData.find(
      (w) => w.WidgetID === parseInt(i)
    );
    const widgetConfig = widgetList.find((w) => w.WidgetID === parseInt(i));
    const widgetName = widgetConfig?.WidgetName || "Details";


    switch (widgetConfig?.WidgetType) {
      case "BarChart":
        return (
          <BarChart
            BarWidgetDetails={BarAlertByType}
            widgetConfig={widgetConfig}
            //handleModalShow={handleModalShow}
            handleModalShow={(filter, API, WidgetID, csvFileName) =>
              handleModalShow(filter, API, WidgetID, csvFileName, widgetName)
            }
            widgetData={widgetDataObj}
            isDarkTheme={isDarkTheme}
          />
        );
      case "DonutChart":
        return (
          <DonutChart
            DonutWidgetDetails={DonutWidgetDetails}
            widgetConfig={widgetConfig}
            // handleModalShow={handleModalShow}
            handleModalShow={(filter, API, WidgetID, csvFileName) =>
              handleModalShow(filter, API, WidgetID, csvFileName, widgetName)
            }
            widgetData={widgetDataObj}
            isDarkTheme={isDarkTheme}
          />
        );
      case "PieChart":
        return (
          <PieChart
            PieWidgetDetails={PieWidgetDetails}
            widgetConfig={widgetConfig}
            // handleModalShow={handleModalShow}
            handleModalShow={(filter, API, WidgetID, csvFileName) =>
              handleModalShow(filter, API, WidgetID, csvFileName, widgetName)
            }
            widgetData={widgetDataObj}
            isDarkTheme={isDarkTheme}
          />
        );
      case "LineChart":
        return (
          <LineChart
            LineWidgetDetails={LineWidgetDetails}
            widgetConfig={widgetConfig}
            // handleModalShow={handleModalShow}
            handleModalShow={(filter, API, WidgetID, csvFileName) =>
              handleModalShow(filter, API, WidgetID, csvFileName, widgetName)
            }
            widgetData={widgetDataObj}
            isDarkTheme={isDarkTheme}
          />
        );
      case "SingleKPI":
        return (
          <SingleValueKPI
            // handleModalShow={handleModalShow}
            handleModalShow={(filter, API, WidgetID, csvFileName, widgetName, Datapoints) =>
              handleModalShow(filter, API, WidgetID, csvFileName, widgetName, Datapoints)
            }
            widgetConfig={widgetConfig}
            widgetData={widgetDataObj}
          />
        );
      case "SingleKPI-Detailed":
        return (
          <SingleValueWithDetailKPI
            // handleModalShow={handleModalShow}
            handleModalShow={(filter, API, WidgetID, csvFileName) =>
              handleModalShow(filter, API, WidgetID, csvFileName, widgetName)
            }
            widgetConfig={widgetConfig}
            widgetData={widgetDataObj}
          />
        );
      case "MultiKPI":
        return (
          <MultipleDetailKPI
            // handleModalShow={handleModalShow}
            handleModalShow={(filter, API, WidgetID, csvFileName, widgetName, Datapoints) =>
              handleModalShow(filter, API, WidgetID, csvFileName, widgetName, Datapoints)
            }
            widgetConfig={widgetConfig}
            widgetData={widgetDataObj}
            isDarkTheme={isDarkTheme}
          />
        );
      case "TimeLine":
        return (
          <BackupActivityTimeline
            widgetConfig={widgetConfig}
            widgetData={widgetDataObj}          
            apiData={VBTimeLineData}            
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            isDarkTheme={!isDarkTheme}

          />
        );
      default:
        return "📦 Widget";
    }
  };

  const filteredData = widgetList?.filter(({ WidgetName }) =>
    WidgetName.toLowerCase().includes(searchText.toLowerCase())
  );

  const dashboardItems = localStorage.getItem("dashboard-items");
  const hasDashboardItems =
    dashboardItems &&
    Array.isArray(JSON.parse(dashboardItems)) &&
    JSON.parse(dashboardItems).length > 0;

  useEffect(() => {
    updateWidgetPayload(items);
  }, [layout, items, counter, updateWidgetPayload]);

  return (
    <div style={{ width: "100%", float: "left" }}>
      <DynamicWidgetList
        open={open}
        setOpen={setOpen}
        searchText={searchText}
        setSearchText={setSearchText}
        filteredData={filteredData}
        items={items}
        handleSelectAllWidgets={handleSelectAllWidgets}
        handleAddWidget={handleAddWidget}
        handleRemoveWidget={handleRemoveWidget}
        layout={layout}
        counter={counter}
        getWidthByWidgetType={getWidthByWidgetType}
        getHeightByWidgetType={getHeightByWidgetType}
        setItems={setItems}
        setLayout={setLayout}
        setCounter={setCounter}
        saveToStorage={saveToStorage}
        widgetList={widgetList}
      />

      {items && items.length ? (
        <div className="widgetBoxContainer">
          <div
            style={{
              width: "100%",
              float: "left",
              overflow: "auto",
              height: "calc(100vh - 210px)",
            }}
          >
            <ResponsiveGridLayout
              className="layout"
              layouts={{ lg: layout }}
              cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
              rowHeight={30}
              isResizable={true}
              resizeHandles={["se"]}
              onLayoutChange={(layout) => {
                onLayoutChange(layout);
              }}
            >
              {items.map((item) => (
                <div key={item.i} className="widget-box">
                  {getWidgetLabel(item.i)}
                  <button
                    onClick={() => handleRemoveWidget(item.i)}
                    className="remove-button"
                  >
                    X
                  </button>
                </div>
              ))}
            </ResponsiveGridLayout>
          </div>
        </div>
      ) : (
        <AlertTemplateMessage
          message={"Add widgets from the dashboard widgets settings panel"}
        />
      )}

      <Modal
        show={show}
        onHide={() => setShow(false)}
        dialogClassName="widgetModelDiv"
        aria-labelledby="example-custom-modal-styling-title"
      >
        <Modal.Header>
          <Modal.Title id="example-custom-modal-styling-title">
             {/* Details {selectedWidgetName ? `${selectedWidgetName}` : ""}  */}
             {selectedWidgetName ? `${selectedWidgetName}` : ""}
          </Modal.Title>
          <img
            src={RemoveIcon}
            alt="Remove"
            style={{ cursor: "pointer" }}
            onClick={() => {
              setShow(false);
            }}
          />
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex mb-4 justify-content-center downloadModal">
            {tableDataLoading ? (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "200px",
                }}
              >
                <span
                  className="spinner-border spinner-border-sm"
                  role="status"
                  aria-hidden="true"
                ></span>
                <span
                  style={{ marginTop: "1rem", color: "#888", fontWeight: 500 }}
                >
                  Loading
                </span>
              </div>
            ) : tableDataError ? (
              <div
                style={{ color: "red", textAlign: "center", marginTop: "2rem" }}
              >
                {tableDataError}
              </div>
            ) : (
              <TableDetails 
                tableData={tableData} 
                csvFileName={csvFileName}
                exportRequestParams={tableRequestParams}
              />
            )}
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DynamicWidgetStructure;
